export const homeHighlights = [
  {
    title: 'Real-Time Music Visuals',
    description:
      'Create responsive music visualizer scenes and audio spectrum visualizer looks that react to your track in real time.'
  },
  {
    title: 'Templates and Presets',
    description:
      'Start with visualizer templates, spectrum-style presets, and reusable looks for different songs, moods, and edits.'
  },
  {
    title: 'Cleaner Exports',
    description:
      'Export polished video content with cleaner output, more reliable rendering, and better premium export options.'
  },
  {
    title: 'Modern iPhone Workflow',
    description:
      'Use a cleaner editing flow built for iPhone, with easier controls, theme support, and faster project handling.'
  }
];

export const featureList = [
  'Multiple visual styles including bars, waves, particles, gradients, and artwork-based scenes',
  'Music visualizer and audio spectrum visualizer workflows for iPhone',
  'Ready-made templates that help you begin a project faster',
  'Visualizer templates and spectrum template workflows for reusable content',
  'Preset switching for quick style comparison before export',
  'Advanced premium presets for more polished looks',
  'Custom preset import, export, duplicate, and management tools',
  'VIZ project import support for preset packages with bundled resources',
  'Real-time music-reactive preview while editing',
  'Built-in editor for changing text, colors, toggles, image sources, and scene options',
  'Photos and Files support for adding custom image assets to your visual scene',
  'Cleaner interface flow for selecting visuals and adjusting settings',
  'Theme support for a more comfortable viewing experience',
  'Cleaner export output with improved rendering reliability',
  'Export controls for destination, file name, resolution, frame rate, bitrate, and time range',
  'Audio export controls for channels and bitrate',
  'Premium export controls and logo-free export support',
  'Library browsing by everything, recently added, albums, artists, genres, and folders',
  'Search, sort, and import tools for your music library',
  'Queue management with reorder, shuffle, repeat, play next, and remove actions',
  'Saved queue lists and add-by-URL support for audio tracks',
  'Equalizer controls with presets, bass, treble, and virtualizer adjustments',
  'Sleep timer with countdown control inside the app',
  'Replayable guided tour and troubleshooting tools in Settings',
  'In-app restore and subscription management flow',
  'Support, privacy, and App Store help pages linked clearly from the product'
];

export const iosDetails = [
  'Available for iPhone with iOS 17.0 and later',
  'Optimized for a modern full-screen iPhone experience',
  'Designed for music preview, preset selection, and export in one flow',
  'Supports premium subscription management directly inside the app',
  'Keeps support, privacy, and subscription help easy to find',
  'Focused on visual creation, clean exports, and a simpler editing experience'
];

export const featureGroups = [
  {
    title: 'Visual Styles',
    description: 'Choose the music visualizer or audio spectrum visualizer look that matches your track and switch styles quickly while previewing.',
    items: [
      'Spectrum-based visual looks for energetic music',
      'Audio spectrum visualizer scenes for music-driven content',
      'Wave and motion-heavy styles for smoother tracks',
      'Particle and glow-driven visuals for richer scenes',
      'Artwork-friendly layouts when you want image-led visuals'
    ]
  },
  {
    title: 'Templates and Presets',
    description: 'Move faster with reusable looks and premium-ready options.',
    items: [
      'Start with ready-made visualizer templates instead of building from zero',
      'Use preset switching to compare different looks quickly',
      'Import VIZ or preset files when you want to bring in a custom scene',
      'Export and duplicate presets for repeatable music visualizer workflows',
      'Save time with custom preset workflows for repeated content',
      'Unlock advanced preset options with Vizo Pro'
    ]
  },
  {
    title: 'Editing Experience',
    description: 'Adjust your visual without losing momentum during playback.',
    items: [
      'Preview scene changes while listening to the track',
      'Tune layout, color, and motion settings in a cleaner flow',
      'Edit text, toggles, select menus, and visual property values directly',
      'Use your own images from Photos or Files in supported scene fields',
      'Work with a more organized interface for faster decisions',
      'Use light and dark aware presentation for better clarity'
    ]
  },
  {
    title: 'Export and Sharing',
    description: 'Prepare content for reels, stories, posts, and music sharing workflows.',
    items: [
      'Export cleaner video results with better consistency',
      'Use premium export options when you need more control',
      'Hide the app logo during export with Pro access',
      'Create videos that are easier to share across social platforms'
    ]
  },
  {
    title: 'Music Library and Queue',
    description: 'Manage what you play and how your playlist moves through the visualizer.',
    items: [
      'Browse your library by songs, recent tracks, albums, artists, genres, and folders',
      'Search and sort your library before starting playback',
      'Build a queue with play next, reorder, remove, shuffle, and repeat controls',
      'Save queue lists and load them again later when you want a reusable set'
    ]
  },
  {
    title: 'Playback Tools',
    description: 'Keep listening and adjusting without leaving the main flow.',
    items: [
      'Use equalizer presets and manual sound controls inside the app',
      'Adjust bass, treble, and virtualizer settings for playback',
      'Set a sleep timer with a live remaining-time preview',
      'Use playback-friendly settings like resume options and audio fade behavior'
    ]
  }
];

export const workflow = [
  'Import a song and preview the scene in real time.',
  'Choose a visualizer template, spectrum preset, or imported VIZ project that fits the mood.',
  'Adjust scene settings, image sources, timing, and playback-related options.',
  'Refine export settings for quality, audio, destination, and time range.',
  'Export a clean shareable video for reels, posts, or music content.'
];

export const premiumFeatures = [
  'Advanced presets and premium visual packs',
  'Custom preset import, export, and duplicate tools',
  'Premium ready-made templates and locked visualizer access',
  'Premium export options including cleaner output control',
  'Hide the app logo during export with Pro access'
];

export const appSections = [
  {
    title: 'Library',
    items: [
      'Import audio files directly into the app',
      'Browse by songs, albums, artists, genres, folders, and recently added',
      'Search and sort your collection before playback'
    ]
  },
  {
    title: 'Queue and Saved Lists',
    items: [
      'Play next, reorder, shuffle, repeat, and clear the queue',
      'Save the current queue as a reusable list',
      'Add audio by URL when you want to queue a direct source'
    ]
  },
  {
    title: 'Templates, Presets, and VIZ',
    items: [
      'Open ready-made visualizer templates for faster project starts',
      'Switch between built-in and premium presets',
      'Import, export, duplicate, and manage custom presets, spectrum presets, and VIZ packages'
    ]
  },
  {
    title: 'Editor',
    items: [
      'Edit scene layers and properties from inside the app',
      'Adjust text, colors, toggles, select options, and visual values',
      'Use custom images from Photos or Files where supported'
    ]
  },
  {
    title: 'Export',
    items: [
      'Control resolution, frame rate, bitrate, file name, destination, and duration',
      'Choose audio export settings such as channels and bitrate',
      'Export cleaner results and hide the app logo with Pro access'
    ]
  },
  {
    title: 'Playback and Settings',
    items: [
      'Use equalizer presets plus bass, treble, and virtualizer controls',
      'Set a sleep timer without leaving the app',
      'Change theme, replay the guided tour, and use troubleshooting tools in Settings'
    ]
  }
];

export const faqs = [
  {
    question: 'How do I get started with Vizo?',
    answer:
      'Open Vizo, import a song, choose a visualizer template or preset, then adjust the scene before exporting your video.'
  },
  {
    question: 'What does Vizo Pro unlock?',
    answer:
      'Vizo Pro unlocks advanced presets, premium templates, custom preset tools, cleaner export options, and logo-free export support.'
  },
  {
    question: 'How do I restore purchases?',
    answer:
      'Open the Vizo Pro screen in the app and use Restore Purchases. If you still have an issue, contact support with your device details.'
  },
  {
    question: 'Why does a preset or VIZ file take time to open?',
    answer:
      'Some preset projects include large images or GIFs. Vizo now loads these more safely, but heavier files may still take a little longer on first open.'
  },
  {
    question: 'Is Vizo useful for people searching for Avee Player-style templates?',
    answer:
      'Yes. Vizo is an independent iPhone music visualizer app, but it is useful for people looking for Avee Player-style visualizer templates, spectrum-style presets, and customizable music visualization workflows.'
  },
  {
    question: 'Can I edit visuals inside the app?',
    answer:
      'Yes. Vizo includes an in-app editor for changing scene properties such as text, colors, toggles, select values, and supported image sources.'
  },
  {
    question: 'Can I manage my queue and save playlists?',
    answer:
      'Yes. You can reorder the queue, use play next, shuffle, repeat, remove tracks, save the queue as a list, and load saved lists later.'
  }
];
